import React from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight, Flame, Dumbbell } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';

export default function CartDrawer({ open, onClose }) {
  const { items, updateQty, removeItem, clearCart, itemCount, subtotal, totalCalories, totalProtein } = useCart();
  const navigate = useNavigate();

  if (!open) return null;

  const handleCheckout = () => {
    if (window.gtag) {
      window.gtag('event', 'begin_checkout', {
        currency: 'USD',
        value: subtotal,
        items: items.map(i => ({
          item_id: i.slug,
          item_name: i.name,
          price: i.price,
          quantity: i.qty,
        })),
      });
    }
    onClose();
    navigate('/checkout');
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50 backdrop-blur-sm" onClick={onClose} data-testid="cart-backdrop" />
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-2xl flex flex-col" data-testid="cart-drawer">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <ShoppingBag size={20} className="text-green-600" />
            <h2 className="text-lg font-bold text-gray-900">Your Cart</h2>
            <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full">{itemCount}</span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="cart-close-btn">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <ShoppingBag size={48} className="text-gray-200 mb-4" />
            <p className="text-gray-500 font-medium mb-2">Your cart is empty</p>
            <p className="text-sm text-gray-400 mb-6">Browse our menu and add your favorite meals</p>
            <Link to="/menu" onClick={onClose} className="bg-green-600 text-white px-6 py-2.5 rounded-lg text-sm font-bold hover:bg-green-700 transition-colors" data-testid="cart-browse-menu-btn">
              Browse Menu
            </Link>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {items.map(item => (
              <div key={item.slug} className="flex gap-3 p-3 bg-gray-50 rounded-xl" data-testid={`cart-item-${item.slug}`}>
                <img src={item.imageUrl} alt={item.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-semibold text-gray-900 truncate">{item.name}</p>
                    <button onClick={() => removeItem(item.slug)} className="p-1 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0" data-testid={`cart-remove-${item.slug}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{item.calories} cal · {item.protein}g protein</p>
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200">
                      <button onClick={() => updateQty(item.slug, item.qty - 1)} className="p-1.5 hover:bg-gray-50 rounded-l-lg transition-colors" data-testid={`cart-qty-minus-${item.slug}`}>
                        <Minus size={12} />
                      </button>
                      <span className="text-sm font-semibold w-7 text-center">{item.qty}</span>
                      <button onClick={() => updateQty(item.slug, item.qty + 1)} className="p-1.5 hover:bg-gray-50 rounded-r-lg transition-colors" data-testid={`cart-qty-plus-${item.slug}`}>
                        <Plus size={12} />
                      </button>
                    </div>
                    <span className="text-sm font-bold text-gray-900">${(item.price * item.qty).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {items.length > 0 && (
          <div className="border-t border-gray-100 px-6 py-4 space-y-3">
            <div className="flex gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1"><Flame size={12} className="text-orange-500" /> {totalCalories} cal</span>
              <span className="flex items-center gap-1"><Dumbbell size={12} className="text-blue-500" /> {totalProtein}g protein</span>
              <span>{itemCount} meals</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Subtotal</span>
              <span className="text-lg font-bold text-gray-900">${subtotal.toFixed(2)}</span>
            </div>
            <p className="text-xs text-green-600 font-medium">Code NINJA saves 20% at checkout</p>
            <button onClick={handleCheckout} className="w-full bg-green-600 text-white py-3 rounded-xl text-sm font-bold hover:bg-green-700 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-green-600/20" data-testid="cart-checkout-btn">
              Proceed to Checkout <ArrowRight size={16} />
            </button>
            <button onClick={clearCart} className="w-full text-xs text-gray-400 hover:text-red-500 transition-colors py-1" data-testid="cart-clear-btn">
              Clear cart
            </button>
          </div>
        )}
      </div>
    </>
  );
}
